// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";

export const gamiSpreadTargetGph = 0.5;
export const gamiSpreadWatchGph = 0.7;
export const gamiSpreadReviewGph = 1.0;

const gamiStrongPeakProminenceF = 6;
const gamiSoftPeakProminenceF = 4;
const gamiPeakEdgeReason = "Peak is at the edge of the marked window";

function finite(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return Number.isFinite(Number(value)) ? Number(value) : null;
}

function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function compactText(value) {
  const text = String(value ?? "").trim();
  return text || null;
}

function rounded(value, digits = 1) {
  return Number.isFinite(value) ? Number(value.toFixed(digits)) : null;
}

function finiteValues(points, key) {
  return points.map((point) => finite(point?.[key])).filter(Number.isFinite);
}

function rangeStats(points, key) {
  const values = finiteValues(points, key);
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((total, value) => total + value, 0) / values.length;
  return {
    key,
    min,
    max,
    avg,
    range: max - min,
    count: values.length,
  };
}

function firstAvailableKey(points, keys) {
  return keys.find((key) => points.some((point) => Number.isFinite(point?.[key]))) ?? null;
}

export function gamiFuelFlowKey(points = []) {
  return firstAvailableKey(points, ["fuelFlow", "fuelFlow1", "fuelFlowLeft", "fuelFlow2", "fuelFlowRight"]);
}

export function gamiEgtKeys(points = []) {
  return cylinderKeys("egt").filter((key) => {
    const count = points.reduce((total, point) => total + (Number.isFinite(point?.[key]) ? 1 : 0), 0);
    return count >= Math.max(3, points.length * 0.35);
  });
}

export function gamiSweepEngineIdentity(resultRow, descriptors = []) {
  const result = objectValue(resultRow?.result_json ?? resultRow?.result ?? resultRow);
  const pilot = objectValue(resultRow?.pilot_context_json ?? resultRow?.pilotContext);
  const engineId = compactText(result.engineId ?? pilot.engineId);
  const engineLabel = compactText(result.engineLabel ?? pilot.engineLabel);
  const matched = engineId
    ? (descriptors ?? []).find((engine) => (
      engine?.id === engineId
      || engine?.engineKey === engineId
      || String(engine?.engineNumber ?? "") === engineId
    ))
    : null;
  if (engineId) {
    return {
      engineId,
      engineLabel: engineLabel ?? compactText(matched?.label) ?? "Engine",
      known: true,
    };
  }
  if ((descriptors?.length ?? 0) <= 1) {
    const single = descriptors?.[0];
    return {
      engineId: compactText(single?.id) ?? "engine1",
      engineLabel: compactText(single?.label) ?? "Engine",
      known: true,
    };
  }
  return {
    engineId: "unknown",
    engineLabel: "Engine not set",
    known: false,
  };
}

function sourceNumberFromPeak(peak) {
  const keyMatch = String(peak?.key ?? "").match(/^(?:egt|cht)(\d+)$/i);
  if (keyMatch) return Number(keyMatch[1]);
  const cylinder = Number(peak?.sourceCylinder ?? peak?.sourceNumber ?? peak?.cylinder);
  return Number.isInteger(cylinder) && cylinder > 0 ? cylinder : null;
}

export function gamiSweepPeakDisplayRows(result, descriptors = []) {
  const peaks = Array.isArray(result?.peaks) ? result.peaks : [];
  return peaks.map((peak) => {
    const sourceNumber = sourceNumberFromPeak(peak);
    let engine = null;
    let cylinder = null;
    for (const candidate of descriptors ?? []) {
      const match = (candidate?.cylinders ?? []).find((item) => item.sourceNumber === sourceNumber);
      if (match) {
        engine = candidate;
        cylinder = match;
        break;
      }
    }
    const displayCylinder = cylinder?.number ?? peak?.cylinder ?? sourceNumber;
    const enginePrefix = engine && (descriptors?.length ?? 0) > 1
      ? String(engine.label ?? engine.shortLabel ?? "Engine").replace(/\s+Engine$/i, "")
      : "";
    return {
      ...peak,
      sourceCylinder: sourceNumber,
      displayCylinder,
      displayCylinderLabel: Number.isFinite(displayCylinder)
        ? `${enginePrefix ? `${enginePrefix} ` : ""}#${displayCylinder}`
        : (peak?.key ?? "--"),
      engineId: engine?.id ?? null,
      engineLabel: engine?.label ?? null,
    };
  });
}

function median(values) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function edgeAverage(values, fromStart = true) {
  const count = Math.max(1, Math.ceil(values.length * 0.14));
  const slice = fromStart ? values.slice(0, count) : values.slice(-count);
  return slice.reduce((total, value) => total + value, 0) / slice.length;
}

function movingAverage(values, index, radius) {
  const start = Math.max(0, index - radius);
  const end = Math.min(values.length - 1, index + radius);
  let total = 0;
  let count = 0;
  for (let cursor = start; cursor <= end; cursor += 1) {
    if (!Number.isFinite(values[cursor])) continue;
    total += values[cursor];
    count += 1;
  }
  return count ? total / count : null;
}

function fuelSweepRows(points, fuelKey) {
  const samples = points
    .filter((point) => Number.isFinite(point?.elapsedSeconds))
    .slice()
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
  const fuelRows = samples
    .map((point, sampleIndex) => ({
      point,
      sampleIndex,
      elapsedSeconds: point.elapsedSeconds,
      fuelFlow: finite(point?.[fuelKey]),
    }))
    .filter((row) => Number.isFinite(row.fuelFlow));
  const searchWindow = {
    startTime: samples[0]?.elapsedSeconds ?? null,
    endTime: samples.at(-1)?.elapsedSeconds ?? null,
    sampleCount: samples.length,
  };
  const smoothRadius = Math.max(1, Math.min(5, Math.floor(fuelRows.length * 0.015)));
  const smoothValues = fuelRows.map((row) => row.fuelFlow);
  const rows = fuelRows.map((row, index) => ({
    ...row,
    fuelIndex: index,
    smoothFuelFlow: movingAverage(smoothValues, index, smoothRadius),
  }));
  return { samples, rows, searchWindow };
}

function boundedSweepFromDrop(samples, rows, searchWindow, best, { guardPostRise = true, endAtBest = false } = {}) {
  if (!best || best.drop < 0.5) return null;

  const edgeFuelTolerance = Math.max(0.01, Math.min(0.03, best.drop * 0.005));
  const minPlateauSeconds = 8;
  const highRows = rows
    .slice(0, best.endIndex + 1)
    .filter((row) => row.smoothFuelFlow >= best.startFuelFlow - edgeFuelTolerance);
  const firstHigh = highRows[0] ?? rows[best.startIndex];
  const lastHigh = highRows.at(-1) ?? rows[best.startIndex];
  const startRow = (lastHigh.elapsedSeconds - firstHigh.elapsedSeconds) >= minPlateauSeconds
    ? lastHigh
    : rows[best.startIndex];

  const lowRows = rows
    .slice(best.startIndex)
    .filter((row) => row.smoothFuelFlow <= best.endFuelFlow + edgeFuelTolerance);
  const firstLow = lowRows[0] ?? rows[best.endIndex];
  const lastLow = lowRows.at(-1) ?? rows[best.endIndex];
  const endRow = endAtBest
    ? rows[best.endIndex]
    : (lastLow.elapsedSeconds - firstLow.elapsedSeconds) >= minPlateauSeconds
    ? firstLow
    : rows[best.endIndex];

  if (!startRow || !endRow || endRow.sampleIndex <= startRow.sampleIndex) {
    return {
      samples,
      detection: {
        adjusted: false,
        reason: "Detected fuel-flow sweep bounds were ambiguous",
        searchWindow,
      },
    };
  }

  if (guardPostRise) {
    const trailingRows = rows.slice(endRow.fuelIndex);
    const trailingMax = trailingRows.length ? Math.max(...trailingRows.map((row) => row.smoothFuelFlow).filter(Number.isFinite)) : null;
    const postSweepRise = Number.isFinite(trailingMax) ? trailingMax - best.endFuelFlow : 0;
    if (postSweepRise >= Math.max(0.4, best.drop * 0.25)) {
      return {
        samples,
        detection: {
          adjusted: false,
          reason: "Fuel flow reverses after the detected drop",
          searchWindow,
        },
      };
    }
  }

  const detected = samples.slice(startRow.sampleIndex, endRow.sampleIndex + 1);
  const adjusted = detected.length >= 5
    && (startRow.sampleIndex > 0 || endRow.sampleIndex < samples.length - 1);
  return {
    samples: detected.length >= 5 ? detected : samples,
    detection: {
      adjusted,
      reason: adjusted ? "Detected sustained fuel-flow drop inside marked window" : "Marked window already matches the fuel-flow sweep",
      searchWindow,
      startTime: startRow.elapsedSeconds,
      endTime: endRow.elapsedSeconds,
      startFuelFlowGph: rounded(startRow.fuelFlow, 2),
      endFuelFlowGph: rounded(endRow.fuelFlow, 2),
      dropGph: rounded(best.drop, 2),
      trimmedStartSeconds: rounded(Math.max(0, startRow.elapsedSeconds - (searchWindow.startTime ?? startRow.elapsedSeconds)), 1),
      trimmedEndSeconds: rounded(Math.max(0, (searchWindow.endTime ?? endRow.elapsedSeconds) - endRow.elapsedSeconds), 1),
    },
  };
}

function detectedSweepSamples(points, fuelKey, boundOptions = {}) {
  const { samples, rows, searchWindow } = fuelSweepRows(points, fuelKey);
  if (rows.length < 8) {
    return {
      samples,
      detection: {
        adjusted: false,
        reason: "Not enough fuel-flow samples to auto-trim",
        searchWindow,
      },
    };
  }

  let best = null;
  let prefixMax = null;
  rows.forEach((row, index) => {
    if (!Number.isFinite(row.smoothFuelFlow)) return;
    if (!prefixMax || row.smoothFuelFlow > prefixMax.value) {
      prefixMax = { index, value: row.smoothFuelFlow };
    }
    if (!prefixMax || prefixMax.index >= index) return;
    const drop = prefixMax.value - row.smoothFuelFlow;
    if (!best || drop > best.drop) {
      best = {
        drop,
        startIndex: prefixMax.index,
        endIndex: index,
        startFuelFlow: prefixMax.value,
        endFuelFlow: row.smoothFuelFlow,
      };
    }
  });

  if (!best || best.drop < 0.5) {
    return {
      samples,
      detection: {
        adjusted: false,
        reason: "No sustained fuel-flow drop found inside marked window",
        searchWindow,
      },
    };
  }

  return boundedSweepFromDrop(samples, rows, searchWindow, best, boundOptions) ?? {
    samples,
    detection: {
      adjusted: false,
      reason: "Detected fuel-flow sweep bounds were ambiguous",
      searchWindow,
    },
  };
}

function detectedSweepSeries(points, fuelKey) {
  const { samples, rows, searchWindow } = fuelSweepRows(points, fuelKey);
  if (rows.length < 8) return [];
  const segments = [];
  let cursor = 0;
  while (cursor < rows.length - 1) {
    let highIndex = cursor;
    let highValue = rows[cursor].smoothFuelFlow;
    let lowIndex = null;
    let lowValue = null;
    let bestDrop = 0;
    let endCursor = null;
    let active = false;

    for (let index = cursor + 1; index < rows.length; index += 1) {
      const value = rows[index].smoothFuelFlow;
      if (!Number.isFinite(value)) continue;
      if (!active) {
        if (!Number.isFinite(highValue) || value > highValue) {
          highIndex = index;
          highValue = value;
        }
        const drop = highValue - value;
        if (drop >= 0.5) {
          active = true;
          lowIndex = index;
          lowValue = value;
          bestDrop = drop;
        }
        continue;
      }

      if (!Number.isFinite(lowValue) || value < lowValue) {
        lowIndex = index;
        lowValue = value;
        bestDrop = highValue - lowValue;
      }
      const resetRise = Math.max(0.4, bestDrop * 0.25);
      if (value - lowValue >= resetRise) {
        endCursor = index;
        break;
      }
    }

    if (!active || lowIndex === null || !Number.isFinite(lowValue)) break;
    const lowSearchEnd = endCursor ?? lowIndex;
    const rawLowRow = rows
      .slice(highIndex, lowSearchEnd + 1)
      .reduce((lowest, row) => (!lowest || row.fuelFlow < lowest.fuelFlow ? row : lowest), null);
    const finalLowIndex = rawLowRow?.fuelIndex ?? lowIndex;
    const best = {
      drop: bestDrop,
      startIndex: highIndex,
      endIndex: finalLowIndex,
      startFuelFlow: highValue,
      endFuelFlow: rows[finalLowIndex]?.smoothFuelFlow ?? lowValue,
    };
    const segment = boundedSweepFromDrop(samples, rows, searchWindow, best, { guardPostRise: false, endAtBest: true });
    if (segment?.samples?.length >= 5) {
      segment.detection = {
        ...segment.detection,
        adjusted: true,
        reason: "Detected repeated sweep inside marked window",
        series: true,
        seriesIndex: segments.length + 1,
      };
      segments.push(segment);
    }
    cursor = Math.max((endCursor ?? lowIndex) + 1, highIndex + 1);
  }
  return segments;
}

function smoothedValue(rows, index, key) {
  const values = [rows[index - 1], rows[index], rows[index + 1]]
    .map((row) => finite(row?.point?.[key]))
    .filter(Number.isFinite);
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : null;
}

function nearestFiniteFuel(rows, index, fuelKey) {
  for (let offset = 0; offset <= 2; offset += 1) {
    const before = finite(rows[index - offset]?.point?.[fuelKey]);
    if (Number.isFinite(before)) return before;
    const after = finite(rows[index + offset]?.point?.[fuelKey]);
    if (Number.isFinite(after)) return after;
  }
  return null;
}

function quadraticPeakElapsedSeconds(rows, index, key) {
  const previous = rows[index - 1];
  const current = rows[index];
  const next = rows[index + 1];
  const currentTime = finite(current?.point?.elapsedSeconds);
  if (!previous || !current || !next || !Number.isFinite(currentTime)) return currentTime;

  const x1 = finite(previous.point?.elapsedSeconds) - currentTime;
  const x3 = finite(next.point?.elapsedSeconds) - currentTime;
  const y1 = finite(previous.point?.[key]);
  const y2 = finite(current.point?.[key]);
  const y3 = finite(next.point?.[key]);
  if (![x1, x3, y1, y2, y3].every(Number.isFinite) || x1 === 0 || x3 === 0 || x1 === x3) {
    return currentTime;
  }

  const d1 = y1 - y2;
  const d3 = y3 - y2;
  const determinant = (x1 ** 2) * x3 - (x3 ** 2) * x1;
  if (Math.abs(determinant) < 1e-9) return currentTime;

  const a = (d1 * x3 - d3 * x1) / determinant;
  const b = ((x1 ** 2) * d3 - (x3 ** 2) * d1) / determinant;
  if (!Number.isFinite(a) || !Number.isFinite(b) || a >= 0) return currentTime;

  const vertexOffset = -b / (2 * a);
  const minOffset = Math.min(x1, 0, x3);
  const maxOffset = Math.max(x1, 0, x3);
  if (!Number.isFinite(vertexOffset) || vertexOffset < minOffset || vertexOffset > maxOffset) return currentTime;
  return currentTime + vertexOffset;
}

function fuelFlowAtElapsed(points, fuelKey, elapsedSeconds) {
  if (!Number.isFinite(elapsedSeconds)) return { value: null, method: "missing", reliable: false, gapSeconds: null };
  const rows = points
    .filter((point) => Number.isFinite(point?.elapsedSeconds) && Number.isFinite(point?.[fuelKey]))
    .map((point) => ({ elapsedSeconds: Number(point.elapsedSeconds), value: Number(point[fuelKey]) }))
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
  if (!rows.length) return { value: null, method: "missing", reliable: false, gapSeconds: null };

  let nearest = rows[0];
  rows.forEach((row) => {
    if (Math.abs(row.elapsedSeconds - elapsedSeconds) < Math.abs(nearest.elapsedSeconds - elapsedSeconds)) nearest = row;
  });

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    if (Math.abs(row.elapsedSeconds - elapsedSeconds) < 1e-9) {
      return { value: row.value, method: "sample", reliable: true, gapSeconds: 0 };
    }
    if (row.elapsedSeconds < elapsedSeconds) continue;
    const before = rows[index - 1];
    const after = row;
    if (!before) break;
    const gapSeconds = after.elapsedSeconds - before.elapsedSeconds;
    if (!Number.isFinite(gapSeconds) || gapSeconds <= 0) break;
    const ratio = (elapsedSeconds - before.elapsedSeconds) / gapSeconds;
    const value = before.value + (after.value - before.value) * ratio;
    return {
      value,
      method: "interpolated",
      reliable: gapSeconds <= 3,
      gapSeconds,
    };
  }

  const distance = Math.abs(nearest.elapsedSeconds - elapsedSeconds);
  return {
    value: distance <= 2 ? nearest.value : null,
    method: "nearest",
    reliable: false,
    gapSeconds: Number.isFinite(distance) ? distance : null,
  };
}

function titAtPeak(points, key, elapsedSeconds) {
  if (!key || !Number.isFinite(elapsedSeconds)) return null;
  const reading = fuelFlowAtElapsed(points, key, elapsedSeconds);
  return reading.reliable ? rounded(reading.value, 0) : null;
}

// Older saved results can recover simultaneous TIT from the engine-scoped log.
export function gamiSweepWithTitAtPeaks(result, points = [], turbocharged = result?.profile?.turbocharged === true) {
  if (!result || !turbocharged) return result;
  const titKey = firstAvailableKey(points, ["tit", "tit1", "tit2"]);
  return {
    ...result,
    profile: { ...result.profile, turbocharged: true },
    peaks: (result.peaks ?? []).map((peak) => ({
      ...peak,
      peakTitF: peak.peakTitF !== undefined ? peak.peakTitF : titAtPeak(points, titKey, peak.peakElapsedSeconds),
    })),
    ...(Array.isArray(result.seriesSweeps) ? {
      seriesSweeps: result.seriesSweeps.map((sweep) => gamiSweepWithTitAtPeaks(sweep, points, true)),
    } : {}),
  };
}

function peakForEgtKey(points, key, fuelKey, titKey = null) {
  const rows = points
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => Number.isFinite(point?.elapsedSeconds) && Number.isFinite(point?.[key]));
  if (rows.length < 5) {
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      clear: false,
      reason: "Not enough EGT samples",
    };
  }

  let peak = null;
  rows.forEach((row, index) => {
    const smooth = smoothedValue(rows, index, key);
    if (!Number.isFinite(smooth)) return;
    if (!peak || smooth > peak.smoothEgt) peak = { ...row, rowIndex: index, smoothEgt: smooth };
  });
  if (!peak) {
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      clear: false,
      reason: "No usable EGT peak",
    };
  }

  const egtValues = rows.map((row) => finite(row.point?.[key])).filter(Number.isFinite);
  const edgeReference = Math.max(edgeAverage(egtValues, true), edgeAverage(egtValues, false));
  const prominenceF = peak.smoothEgt - edgeReference;
  const edgeGuard = Math.max(1, Math.min(3, Math.floor(rows.length * 0.05)));
  const hasPeakNeighbors = peak.rowIndex > 0 && peak.rowIndex < rows.length - 1;
  const nearWindowEdge = peak.rowIndex < edgeGuard || peak.rowIndex > rows.length - 1 - edgeGuard;
  const localCrest = hasPeakNeighbors
    && peak.smoothEgt >= (smoothedValue(rows, peak.rowIndex - 1, key) ?? -Infinity)
    && peak.smoothEgt >= (smoothedValue(rows, peak.rowIndex + 1, key) ?? -Infinity);
  const peakAtEdge = !hasPeakNeighbors || (nearWindowEdge && !localCrest);
  const sampleFuelFlow = nearestFiniteFuel(rows, peak.rowIndex, fuelKey);
  const peakElapsedSeconds = quadraticPeakElapsedSeconds(rows, peak.rowIndex, key);
  const fuelFlow = fuelFlowAtElapsed(points, fuelKey, peakElapsedSeconds);
  const peakFuelFlow = Number.isFinite(fuelFlow.value) ? fuelFlow.value : sampleFuelFlow;
  const fuelReliable = fuelFlow.reliable;
  const strongPeak = prominenceF >= gamiStrongPeakProminenceF;
  const softPeak = prominenceF >= gamiSoftPeakProminenceF;
  const peakStrength = strongPeak ? "strong" : softPeak ? "soft" : "weak";
  const clear = !peakAtEdge && softPeak && Number.isFinite(peakFuelFlow) && fuelReliable;
  const reason = clear
    ? ""
    : peakAtEdge
      ? gamiPeakEdgeReason
      : !fuelReliable && Number.isFinite(peakFuelFlow)
        ? "Fuel-flow interpolation gap is too wide"
        : "EGT peak is weak or fuel flow is missing";

  return {
    key,
    cylinder: cylinderNumberFromKey(key),
    clear,
    reason,
    peakEgtF: rounded(finite(peak.point?.[key]), 0),
    peakFuelFlowGph: rounded(peakFuelFlow, 2),
    ...(titKey ? { peakTitF: titAtPeak(points, titKey, peakElapsedSeconds) } : {}),
    peakElapsedSeconds: rounded(peakElapsedSeconds, 2),
    peakSampleElapsedSeconds: peak.point.elapsedSeconds,
    peakFuelFlowMethod: fuelFlow.method,
    peakFuelFlowGapSeconds: rounded(fuelFlow.gapSeconds, 2),
    prominenceF: rounded(prominenceF, 0),
    peakStrength,
  };
}

function fuelFlowTrend(points, fuelKey) {
  const rows = points
    .filter((point) => Number.isFinite(point?.elapsedSeconds) && Number.isFinite(point?.[fuelKey]))
    .map((point) => ({ elapsedSeconds: point.elapsedSeconds, value: Number(point[fuelKey]) }));
  if (rows.length < 3) return null;

  const edgeCount = Math.max(1, Math.ceil(rows.length * 0.12));
  const start = median(rows.slice(0, edgeCount).map((row) => row.value));
  const end = median(rows.slice(-edgeCount).map((row) => row.value));
  let decreasing = 0;
  let increasing = 0;
  let previousSign = 0;
  let reversals = 0;
  for (let index = 1; index < rows.length; index += 1) {
    const delta = rows[index].value - rows[index - 1].value;
    if (Math.abs(delta) < 0.01) continue;
    const sign = Math.sign(delta);
    if (sign < 0) decreasing += 1;
    if (sign > 0) increasing += 1;
    if (previousSign && sign !== previousSign) reversals += 1;
    previousSign = sign;
  }
  const meaningfulSteps = decreasing + increasing;
  return {
    key: fuelKey,
    startGph: rounded(start, 2),
    endGph: rounded(end, 2),
    dropGph: rounded(Number.isFinite(start) && Number.isFinite(end) ? start - end : null, 2),
    minGph: rounded(Math.min(...rows.map((row) => row.value)), 2),
    maxGph: rounded(Math.max(...rows.map((row) => row.value)), 2),
    monotonicShare: meaningfulSteps ? rounded(decreasing / meaningfulSteps, 2) : null,
    reversalCount: reversals,
    sampleCount: rows.length,
  };
}

function spreadBand(spreadGph) {
  if (!Number.isFinite(spreadGph)) return "unknown";
  if (spreadGph <= gamiSpreadTargetGph) return "target";
  if (spreadGph <= gamiSpreadWatchGph) return "watch";
  if (spreadGph <= gamiSpreadReviewGph) return "review";
  return "wide";
}

function sweepDurationBand(durationSeconds, { turbocharged = false, titManaged = false } = {}) {
  if (!Number.isFinite(durationSeconds)) return "unknown";
  if (durationSeconds >= 150) return "deliberate";
  if (turbocharged && titManaged && durationSeconds >= 25) return "turbo-fast";
  if (durationSeconds >= 45) return "short";
  return "too-fast";
}

function confidenceRank(confidence) {
  return { Low: 0, Medium: 1, High: 2 }[confidence] ?? 0;
}

function confidenceFromRank(rank) {
  if (rank >= 2) return "High";
  if (rank >= 1) return "Medium";
  return "Low";
}

function optionEnabled(options, key) {
  return options?.[key] === true;
}

function procedureContext(options = {}, { fuel, peakFuelFlows = [], clearPeakCount = 0, peakCount = 0 } = {}) {
  const firstPeakFuelFlow = peakFuelFlows.length ? Math.max(...peakFuelFlows) : null;
  const lastPeakFuelFlow = peakFuelFlows.length ? Math.min(...peakFuelFlows) : null;
  const richStartMarginGph = Number.isFinite(fuel?.startGph) && Number.isFinite(firstPeakFuelFlow)
    ? rounded(fuel.startGph - firstPeakFuelFlow, 2)
    : null;
  const leanFinishMarginGph = Number.isFinite(fuel?.endGph) && Number.isFinite(lastPeakFuelFlow)
    ? rounded(lastPeakFuelFlow - fuel.endGph, 2)
    : null;
  const startedRichByData = Number.isFinite(richStartMarginGph) ? richStartMarginGph >= 0.2 : null;
  const completedLeanByData = Number.isFinite(leanFinishMarginGph) ? leanFinishMarginGph >= 0.1 : null;
  const allPeaksClear = peakCount > 0 && clearPeakCount === peakCount;
  const context = {
    intentionalSweep: optionEnabled(options, "intentionalSweep"),
    startedRich: optionEnabled(options, "startedRich"),
    completedToPeaks: optionEnabled(options, "completedToPeaks"),
    repeatedSweeps: optionEnabled(options, "repeatedSweeps"),
    titManaged: optionEnabled(options, "titManaged"),
    startedRichByData,
    completedLeanByData,
    richStartMarginGph,
    leanFinishMarginGph,
    allPeaksClear,
  };
  const startedRichSupported = context.startedRichByData === true
    || (context.startedRich && context.startedRichByData !== false);
  const completedSupported = context.allPeaksClear || context.completedToPeaks || context.completedLeanByData === true;
  return {
    ...context,
    startedRichSupported,
    completedSupported,
    confidenceSupportCount: [
      context.intentionalSweep,
      startedRichSupported,
      completedSupported,
      context.repeatedSweeps,
      context.titManaged,
    ].filter(Boolean).length,
  };
}

function procedureSupportsConfidence(procedure) {
  return procedure.intentionalSweep && procedure.startedRichSupported && procedure.completedSupported;
}

function confidenceReason({
  confidence,
  status,
  clearPeakCount,
  peakCount,
  enoughFuelDrop,
  mostlyMonotonic,
  powerStable,
  durationBand,
  procedure,
  procedureBoosted,
}) {
  const reasons = [];
  if (clearPeakCount < peakCount) {
    reasons.push(procedure?.completedToPeaks
      ? `Pilot completion cue; ${clearPeakCount}/${peakCount} peaks`
      : `${clearPeakCount}/${peakCount} clear peaks`);
  }
  if (!enoughFuelDrop) reasons.push("Fuel flow did not drop enough");
  if (enoughFuelDrop && !mostlyMonotonic) reasons.push("Fuel flow was choppy");
  if (!powerStable) reasons.push("Power moved during the sweep");
  if (procedure?.startedRich && procedure.startedRichByData === false) reasons.push("Marked window starts past peak");
  if (durationBand === "too-fast") reasons.push("Sweep was very short");
  if (durationBand === "short" && !procedureBoosted) reasons.push("Shorter than ideal duration");
  if (reasons.length) return reasons[0];
  if (procedureBoosted) return procedure?.repeatedSweeps ? "Repeated procedure context" : "Pilot context supports short sweep";
  if (confidence === "High") return "All core sweep checks passed";
  if (status === "partial") return "Partial sweep evidence";
  return "Sweep shape is ambiguous";
}

function powerStability(points) {
  const percentPower = rangeStats(points, "percentPower");
  const rpm = firstAvailableKey(points, ["rpm", "rpm1", "rpmLeft"]);
  const map = firstAvailableKey(points, ["map", "map1", "mapLeft"]);
  const rpmStats = rpm ? rangeStats(points, rpm) : null;
  const mapStats = map ? rangeStats(points, map) : null;
  const percentStable = !percentPower || percentPower.range <= 4;
  const rpmStable = !rpmStats || rpmStats.range <= 90;
  const mapStable = !mapStats || mapStats.range <= 1.2;
  return {
    stable: percentStable && rpmStable && mapStable,
    percentPower: percentPower ? {
      avg: rounded(percentPower.avg, 0),
      range: rounded(percentPower.range, 1),
    } : null,
    rpm: rpmStats ? {
      key: rpm,
      avg: rounded(rpmStats.avg, 0),
      range: rounded(rpmStats.range, 0),
    } : null,
    map: mapStats ? {
      key: map,
      avg: rounded(mapStats.avg, 1),
      range: rounded(mapStats.range, 1),
    } : null,
  };
}

function profileApplicability(aircraftProfile = null) {
  const fuelSystem = String(aircraftProfile?.engine?.fuelSystem ?? "").trim();
  const injected = /inject/i.test(fuelSystem);
  const carbureted = /carb/i.test(fuelSystem);
  return {
    fuelSystem: fuelSystem || "",
    fuelInjected: injected,
    carbureted,
    turbocharged: aircraftProfile?.turbo?.isTurbo === true,
  };
}

function representativeSeriesSweep(sweeps = []) {
  return sweeps
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const spreadDelta = (Number.isFinite(b?.spreadGph) ? b.spreadGph : -Infinity)
        - (Number.isFinite(a?.spreadGph) ? a.spreadGph : -Infinity);
      if (spreadDelta) return spreadDelta;
      const confidenceDelta = confidenceRank(b?.confidence) - confidenceRank(a?.confidence);
      if (confidenceDelta) return confidenceDelta;
      return (a?.startTime ?? 0) - (b?.startTime ?? 0);
    })[0] ?? null;
}

export function analyzeMarkedGamiSweep(points = [], options = {}) {
  const searchSamples = points
    .filter((point) => Number.isFinite(point?.elapsedSeconds))
    .slice()
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
  const profile = profileApplicability(options.aircraftProfile);
  const fuelKey = gamiFuelFlowKey(searchSamples);
  if (fuelKey && options.repeatedSweeps === true && options.__singleSweepOnly !== true) {
    const segments = detectedSweepSeries(searchSamples, fuelKey);
    if (segments.length > 1) {
      const seriesSweeps = segments.map((segment, index) => {
        const sweep = analyzeMarkedGamiSweep(segment.samples, {
          ...options,
          __singleSweepOnly: true,
        });
        return {
          ...sweep,
          seriesIndex: index + 1,
          seriesSweepCount: segments.length,
          sweepDetection: {
            ...segment.detection,
            resultStartTime: sweep.startTime,
            resultEndTime: sweep.endTime,
          },
        };
      });
      const representative = representativeSeriesSweep(seriesSweeps) ?? seriesSweeps[0];
      return {
        ...representative,
        label: "Detected sweep series",
        seriesSweeps,
        seriesSweepCount: seriesSweeps.length,
        searchSampleCount: searchSamples.length,
        sweepDetection: {
          adjusted: true,
          series: true,
          reason: `Detected ${seriesSweeps.length} repeated sweeps inside marked window`,
          searchWindow: {
            startTime: searchSamples[0]?.elapsedSeconds ?? null,
            endTime: searchSamples.at(-1)?.elapsedSeconds ?? null,
            sampleCount: searchSamples.length,
          },
          representativeIndex: representative?.seriesIndex ?? 1,
        },
      };
    }
  }
  const {
    samples,
    detection: sweepDetection,
  } = fuelKey
    ? detectedSweepSamples(searchSamples, fuelKey)
    : {
      samples: searchSamples,
      detection: {
        adjusted: false,
        reason: "Fuel-flow channel unavailable",
        searchWindow: {
          startTime: searchSamples[0]?.elapsedSeconds ?? null,
          endTime: searchSamples.at(-1)?.elapsedSeconds ?? null,
          sampleCount: searchSamples.length,
        },
      },
    };
  const startTime = samples[0]?.elapsedSeconds ?? null;
  const endTime = samples.at(-1)?.elapsedSeconds ?? null;
  const durationSeconds = Number.isFinite(startTime) && Number.isFinite(endTime) ? endTime - startTime : null;
  const egtKeys = gamiEgtKeys(samples);
  const missing = [];
  if (!fuelKey) missing.push("fuel-flow channel");
  if (egtKeys.length < 2) missing.push("multiple EGT channels");
  if (samples.length < 5) missing.push("enough samples");

  if (missing.length) {
    return {
      status: "missing-data",
      label: "Data limited",
      missing,
      startTime,
      endTime,
      durationSeconds,
      sampleCount: samples.length,
      searchSampleCount: searchSamples.length,
      sweepDetection,
      procedure: procedureContext(options),
      profile,
    };
  }

  const fuel = fuelFlowTrend(samples, fuelKey);
  const titKey = profile.turbocharged ? firstAvailableKey(samples, ["tit", "tit1", "tit2"]) : null;
  const peaks = egtKeys.map((key) => peakForEgtKey(samples, key, fuelKey, titKey));
  const peaksWithFuel = peaks.filter((peak) => Number.isFinite(peak.peakFuelFlowGph));
  const clearPeaks = peaks.filter((peak) => peak.clear);
  const peakFuelFlows = peaksWithFuel.map((peak) => peak.peakFuelFlowGph);
  const spreadGph = peakFuelFlows.length >= 2 ? Math.max(...peakFuelFlows) - Math.min(...peakFuelFlows) : null;
  const complete = clearPeaks.length === egtKeys.length;
  const enoughFuelDrop = Number.isFinite(fuel?.dropGph) && fuel.dropGph >= 0.7;
  const mostlyMonotonic = Number.isFinite(fuel?.monotonicShare) && fuel.monotonicShare >= 0.72;
  const procedure = procedureContext(options, {
    fuel,
    peakFuelFlows,
    clearPeakCount: clearPeaks.length,
    peakCount: egtKeys.length,
  });
  const durationBand = sweepDurationBand(durationSeconds, {
    turbocharged: profile.turbocharged,
    titManaged: procedure.titManaged,
  });
  const power = powerStability(samples);
  const procedureSupported = procedureSupportsConfidence(procedure);
  const nearCompletePeaks = clearPeaks.length >= Math.max(2, egtKeys.length - 2);

  let status = "low-confidence";
  if (complete && enoughFuelDrop && mostlyMonotonic && power.stable) status = "complete";
  else if (clearPeaks.length >= Math.max(2, egtKeys.length - 1) && enoughFuelDrop) status = "partial";
  else if (nearCompletePeaks && enoughFuelDrop && mostlyMonotonic && power.stable && procedureSupported) status = "partial";

  let confidence = "Low";
  if (status === "complete" && (durationBand === "deliberate" || durationBand === "turbo-fast")) confidence = "High";
  else if (status === "complete" || status === "partial") confidence = "Medium";
  const procedureBoosted = confidence === "Medium"
    && status === "complete"
    && durationBand === "short"
    && mostlyMonotonic
    && power.stable
    && procedureSupported
    && procedure.confidenceSupportCount >= 3;
  if (procedureBoosted) confidence = confidenceFromRank(confidenceRank(confidence) + 1);
  const confidenceDetail = confidenceReason({
    confidence,
    status,
    clearPeakCount: clearPeaks.length,
    peakCount: egtKeys.length,
    enoughFuelDrop,
    mostlyMonotonic,
    powerStable: power.stable,
    durationBand,
    procedure,
    procedureBoosted,
  });

  return {
    status,
    label: status === "complete" ? "Confirmed marked sweep" : status === "partial" ? "Partial marked sweep" : "Low-confidence marked sweep",
    confidence,
    confidenceDetail,
    startTime,
    endTime,
    durationSeconds,
    sampleCount: samples.length,
    searchSampleCount: searchSamples.length,
    sweepDetection,
    fuelKey,
    egtKeys,
    fuel,
    peaks,
    clearPeakCount: clearPeaks.length,
    peakCount: egtKeys.length,
    spreadGph: rounded(spreadGph, 2),
    spreadBand: spreadBand(spreadGph),
    complete,
    durationBand,
    power,
    procedure,
    profile,
  };
}

function leaningSweepPeak(peak) {
  const method = peak?.peakFuelFlowMethod ?? "missing";
  const gapSeconds = Number.isFinite(peak?.peakFuelFlowGapSeconds) ? peak.peakFuelFlowGapSeconds : null;
  return {
    key: peak.key,
    cylinder: peak.cylinder ?? null,
    clear: peak.clear === true,
    reason: peak.reason ?? "",
    atEdge: peak.reason === gamiPeakEdgeReason,
    peakValue: Number.isFinite(peak.peakEgtF) ? peak.peakEgtF : null,
    peakTime: Number.isFinite(peak.peakElapsedSeconds) ? peak.peakElapsedSeconds : null,
    peakSampleTime: Number.isFinite(peak.peakSampleElapsedSeconds) ? peak.peakSampleElapsedSeconds : null,
    peakFuelFlow: Number.isFinite(peak.peakFuelFlowGph) ? peak.peakFuelFlowGph : null,
    ...(peak.peakTitF !== undefined ? { peakTit: peak.peakTitF } : {}),
    prominenceF: Number.isFinite(peak.prominenceF) ? peak.prominenceF : null,
    peakStrength: peak.peakStrength ?? null,
    fuelFlowMethod: method,
    interpolationGapSeconds: gapSeconds,
    fuelFlowReliable: method === "sample" || (method === "interpolated" && Number.isFinite(gapSeconds) && gapSeconds <= 3),
  };
}

// Shared leaning-sweep measurement for the mixture context detectors. It reuses
// the GAMI sweep internals (fuel-flow bounding, raw-crest EGT peaks with fuel flow
// at peak, fuel trend, power) without changing analyzeMarkedGamiSweep. Pure: the
// input points are never mutated. An empty measurement (sweep null, no peaks)
// means no bounded fuel-flow drop was found in the window.
//
// peakSearchTailSeconds lets a crest that sits on the END edge of the bounded
// fuel-flow drop be searched again over the drop plus that many seconds of the
// window after it. EGT probes lag fuel flow by a few seconds, so when a pull
// stops right after a cylinder peaks, that cylinder's crest lands on or after the
// last sweep row and would otherwise be rejected as still rising. Crests found
// inside the drop keep the in-drop reading (an EGT rising again later in the hold
// must not displace them). The sweep bounds themselves are unchanged and the
// default of 0 keeps the GAMI behavior.
export function measureLeaningSweep(points = [], {
  fuelKey = null, egtKeys = null, titKey = null, guardPostRise = true, peakSearchTailSeconds = 0,
} = {}) {
  const searchSamples = points
    .filter((point) => Number.isFinite(point?.elapsedSeconds))
    .slice()
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
  const resolvedFuelKey = fuelKey ?? gamiFuelFlowKey(searchSamples);
  const requestedEgtKeys = Array.isArray(egtKeys) && egtKeys.length ? egtKeys.slice() : null;
  const emptyMeasurement = (detection) => ({
    fuelKey: resolvedFuelKey,
    egtKeys: requestedEgtKeys ?? gamiEgtKeys(searchSamples),
    titKey: titKey ?? null,
    detection,
    sweep: null,
    peaks: [],
    clearPeakCount: 0,
    spreadGph: null,
    fuelTrend: null,
    power: powerStability(searchSamples),
  });
  if (!resolvedFuelKey) {
    return emptyMeasurement({
      adjusted: false,
      reason: "Fuel-flow channel unavailable",
      searchWindow: {
        startTime: searchSamples[0]?.elapsedSeconds ?? null,
        endTime: searchSamples.at(-1)?.elapsedSeconds ?? null,
        sampleCount: searchSamples.length,
      },
    });
  }

  const { samples, detection } = detectedSweepSamples(searchSamples, resolvedFuelKey, { guardPostRise });
  if (!Number.isFinite(detection?.startTime) || !Number.isFinite(detection?.endTime)) {
    return emptyMeasurement(detection);
  }

  const resolvedEgtKeys = requestedEgtKeys ?? gamiEgtKeys(samples);
  const fuelTrend = fuelFlowTrend(samples, resolvedFuelKey);
  const startTime = samples[0]?.elapsedSeconds ?? null;
  const endTime = samples.at(-1)?.elapsedSeconds ?? null;
  const tailSeconds = Number.isFinite(peakSearchTailSeconds) ? Math.max(0, peakSearchTailSeconds) : 0;
  const tailSamples = tailSeconds > 0 && Number.isFinite(startTime) && Number.isFinite(endTime)
    ? searchSamples.filter((point) => point.elapsedSeconds >= startTime && point.elapsedSeconds <= endTime + tailSeconds)
    : null;
  const crestOnEndEdge = (peak) => peak.reason === gamiPeakEdgeReason
    && Number.isFinite(peak.peakSampleElapsedSeconds)
    && peak.peakSampleElapsedSeconds >= (startTime + endTime) / 2;
  const peaks = resolvedEgtKeys.map((key) => {
    const inSweep = peakForEgtKey(samples, key, resolvedFuelKey, titKey ?? null);
    const peak = tailSamples && tailSamples.length > samples.length && crestOnEndEdge(inSweep)
      ? peakForEgtKey(tailSamples, key, resolvedFuelKey, titKey ?? null)
      : inSweep;
    return leaningSweepPeak(peak);
  });
  const clearPeaks = peaks.filter((peak) => peak.clear && Number.isFinite(peak.peakFuelFlow));
  const clearFuelFlows = clearPeaks.map((peak) => peak.peakFuelFlow);
  return {
    fuelKey: resolvedFuelKey,
    egtKeys: resolvedEgtKeys,
    titKey: titKey ?? null,
    detection,
    sweep: {
      startTime,
      endTime,
      durationSeconds: Number.isFinite(startTime) && Number.isFinite(endTime) ? endTime - startTime : null,
      sampleCount: samples.length,
      startGph: fuelTrend?.startGph ?? null,
      endGph: fuelTrend?.endGph ?? null,
      minGph: fuelTrend?.minGph ?? null,
      dropGph: fuelTrend?.dropGph ?? null,
      monotonicShare: fuelTrend?.monotonicShare ?? null,
      reversalCount: fuelTrend?.reversalCount ?? null,
    },
    peaks,
    clearPeakCount: clearPeaks.length,
    spreadGph: clearFuelFlows.length >= 2 ? rounded(Math.max(...clearFuelFlows) - Math.min(...clearFuelFlows), 2) : null,
    fuelTrend,
    power: powerStability(samples),
  };
}
